import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  CircularProgress,
  Button,
  FormHelperText,
  Typography,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { vehicleServices, apiCategory, brandServices } from "../../services/AllServices";
import { IMAGE_BASE } from "../../utils/constants";
import { validationSchema } from "../../utils/Schema";
import AxiosInterceptor from "../../utils/AxiosInterceptor";
import GooglePlacesAutocomplete from "../../components/GooglePlacesAutocomplete";
import LocationPickerModal from "../../components/LocationPickerModal";

function AddEditVehicle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [certificates, setCertificates] = useState([]);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const certificateInputRef = useRef(null);
  const [conditionReports, setConditionReports] = useState([]);
  const [conditionReportFiles, setConditionReportFiles] = useState([]);
  const [conditionReportType, setConditionReportType] = useState('document');
  const [conditionReportLink, setConditionReportLink] = useState('');
  const conditionReportInputRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await apiCategory.getAll({
        pageNumber: 1,
        pageSize: 100,
        status: [1]
      });
      if (response?.status === 200) {
        setCategories(response?.data?.data?.categories || []);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchSubCategories = useCallback(async (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) {
      setSubCategories([]);
      return;
    }

    try {
      setSubCategoriesLoading(true);
      const response = await apiCategory.getSubCategory({
        id: categoryIds
      });
      if (response?.status === 200) {
        setSubCategories(response?.data?.data?.subCategories || []);
      }
    } catch (error) {
      errorHandler(error);
      setSubCategories([]);
    } finally {
      setSubCategoriesLoading(false);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await brandServices.getAll({
        pageNumber: 1,
        pageSize: 100,
        status: [1]
      });
      if (response?.status === 200) {
        setBrands(response?.data?.data?.brands || []);
      }
    } catch (error) {
      errorHandler(error);
    }
  }, []);

  const fetchDealers = useCallback(async () => {
    try {
      setDealersLoading(true);
      const response = await AxiosInterceptor().post(`/getAllDealer`, {
        pageNumber: 1,
        pageSize: 100,
        status: [1],
      });
      const list = response?.data?.data?.result || [];
      setDealers(Array.isArray(list) ? list : []);
    } catch (error) {
      errorHandler(error);
      setDealers([]);
    } finally {
      setDealersLoading(false);
    }
  }, []);


  const fetchVehicleData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await vehicleServices.getVehicleById({ id });
        const vehicleData = response?.data?.data;
        setData(vehicleData);

        if (vehicleData?.vehicleImage) {
          setImages(vehicleData.vehicleImage);
        }


        let certs = [];

        if (vehicleData?.vehicleDocuments) {
          // Handle new vehicleDocuments array structure
          certs = vehicleData.vehicleDocuments.map(doc => ({
            name: doc.name || '',
            fileName: doc.image || '',
            file: null,
            type: doc.vehicleDocumentsLink ? 'link' : 'document',
            link: doc.vehicleDocumentsLink || '',
            _id: doc._id
          }));
        } else if (vehicleData?.certificate) {
          // Handle old single certificate format and new multiple certificates format
          if (Array.isArray(vehicleData.certificate)) {
            certs = vehicleData.certificate.map(cert => ({
              name: cert.name || '',
              fileName: cert.fileName || cert,
              file: null,
              type: cert.link ? 'link' : 'document',
              link: cert.link || ''
            }));
          } else {
            certs = [{
              name: '',
              fileName: vehicleData.certificate,
              file: null,
              type: 'document',
              link: ''
            }];
          }
        }

        // Handle vehicleDocumentsLink - merge with existing certificates
        if (vehicleData?.vehicleDocumentsLink) {
          const links = Array.isArray(vehicleData.vehicleDocumentsLink)
            ? vehicleData.vehicleDocumentsLink
            : [vehicleData.vehicleDocumentsLink];

          // Update existing certificates with links or add new ones
          links.forEach((link, idx) => {
            if (certs[idx]) {
              // Update existing certificate with link if it doesn't have a file
              if (!certs[idx].file && !certs[idx].fileName) {
                certs[idx] = {
                  ...certs[idx],
                  type: 'link',
                  link: link
                };
              }
            } else {
              // Add new certificate with link
              certs.push({
                name: '',
                fileName: '',
                file: null,
                type: 'link',
                link: link
              });
            }
          });
        }

        setCertificates(certs);

        if (vehicleData?.conditionReport) {
          setConditionReports([vehicleData.conditionReport]);
          setConditionReportType('document');
        }

        if (vehicleData?.conditionReportLink) {
          setConditionReportLink(vehicleData.conditionReportLink);
          setConditionReportType('link');
          setConditionReports([]);
          setConditionReportFiles([]);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);


  useEffect(() => {
    fetchVehicleData();
    fetchCategories();
    fetchBrands();
    fetchDealers();
  }, [fetchVehicleData, fetchCategories, fetchBrands, fetchDealers]);

  useEffect(() => {
    if (data?.category && data.category.length > 0 && categories.length > 0) {
      fetchSubCategories(data.category);
    }
  }, [data, categories, fetchSubCategories]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Allowed file types
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];

      // Validate file types
      const invalidFiles = files.filter(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return !allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension);
      });

      if (invalidFiles.length > 0) {
        toast.error(`Invalid file format. Only PNG, JPG, JPEG, and WEBP formats are allowed.`);
        e.target.value = ''; // Clear the input
        return;
      }

      setImageLoading(true);
      const validFiles = files.filter(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
      });

      const newImageFiles = [...imageFiles, ...validFiles];
      setImageFiles(newImageFiles);

      const newImages = [...images];
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result);
          setImages([...newImages]);
          setFieldValue("vehicleImages", [...newImages]);
          setImageLoading(false);
        };
        reader.readAsDataURL(file);
      });
    }
  };



  const handleCertificateChange = (e, index) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      const newCertificates = [...certificates];
      const newCertificateFiles = [...certificateFiles];

      if (index !== undefined) {
        // Update existing certificate
        newCertificates[index] = {
          ...newCertificates[index],
          fileName: file.name,
          file: file,
          type: 'document',
          link: ''
        };
        newCertificateFiles[index] = file;
      } else {
        newCertificates.push({
          name: '',
          fileName: file.name,
          file: file,
          type: 'document',
          link: ''
        });
        newCertificateFiles.push(file);
      }

      setCertificates(newCertificates);
      setCertificateFiles(newCertificateFiles);
    }
    e.target.value = '';
  };

  const handleCertificateTypeChange = (index, type) => {
    const newCertificates = [...certificates];
    newCertificates[index] = {
      ...newCertificates[index],
      type: type,
      file: type === 'link' ? null : newCertificates[index].file,
      fileName: type === 'link' ? '' : newCertificates[index].fileName,
      link: type === 'document' ? '' : newCertificates[index].link
    };
    setCertificates(newCertificates);

    if (type === 'document') {
      const newCertificateFiles = [...certificateFiles];
      newCertificateFiles[index] = null;
      setCertificateFiles(newCertificateFiles);
    }
  };

  const handleCertificateLinkChange = (index, link) => {
    const newCertificates = [...certificates];
    newCertificates[index] = {
      ...newCertificates[index],
      link: link
    };
    setCertificates(newCertificates);
  };

  const handleConditionReportChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      setConditionReportFiles([file]);
      setConditionReports([file.name]);
      setConditionReportLink('');
    }
    e.target.value = '';
  };

  const handleConditionReportTypeChange = (type) => {
    setConditionReportType(type);
    if (type === 'link') {
      setConditionReportFiles([]);
      setConditionReports([]);
    } else {
      setConditionReportLink('');
    }
  };

  const removeImage = (index) => {
    const removedImage = images[index];
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    setFieldValue("vehicleImages", newImages);

    if (removedImage.startsWith("data:")) {
      const fileIndex = index - (images.length - imageFiles.length);
      if (fileIndex >= 0) {
        const newImageFiles = imageFiles.filter((_, i) => i !== fileIndex);
        setImageFiles(newImageFiles);
      }
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    const isDataUrl = draggedImage.startsWith("data:");

    // Remove the dragged image from its current position
    newImages.splice(draggedIndex, 1);
    // Insert it at the new position
    newImages.splice(dropIndex, 0, draggedImage);

    setImages(newImages);
    setFieldValue("vehicleImages", newImages);

    // Also reorder imageFiles if the dragged image is a newly uploaded file
    if (isDataUrl && imageFiles.length > 0) {
      // Build mapping of image indices to file indices BEFORE move
      const imageIndexToFileIndex = new Map();
      let fileIndex = 0;
      for (let i = 0; i < images.length; i++) {
        if (images[i].startsWith("data:")) {
          imageIndexToFileIndex.set(i, fileIndex);
          fileIndex++;
        }
      }

      const draggedFileIndex = imageIndexToFileIndex.get(draggedIndex);

      if (draggedFileIndex !== undefined) {
        const newImageFiles = [...imageFiles];
        const draggedFile = newImageFiles[draggedFileIndex];

        // Remove the file from its current position
        newImageFiles.splice(draggedFileIndex, 1);

        // Calculate new file position
        let newFileIndex = 0;
        for (let i = 0; i < dropIndex; i++) {
          if (newImages[i].startsWith("data:")) {
            newFileIndex++;
          }
        }

        // Insert the file at the new position
        newImageFiles.splice(newFileIndex, 0, draggedFile);
        setImageFiles(newImageFiles);
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };



  const removeCertificate = (index) => {
    const newCertificates = certificates.filter((_, i) => i !== index);
    const newCertificateFiles = certificateFiles.filter((_, i) => i !== index);
    setCertificates(newCertificates);
    setCertificateFiles(newCertificateFiles);
  };

  const addNewCertificate = () => {
    const newCertificates = [...certificates, {
      name: '',
      fileName: '',
      file: null,
      type: 'document',
      link: ''
    }];
    setCertificates(newCertificates);
  };

  const updateCertificateName = (index, name) => {
    const newCertificates = [...certificates];
    newCertificates[index] = { ...newCertificates[index], name };
    setCertificates(newCertificates);
  };

  const removeConditionReport = () => {
    setConditionReports([]);
    setConditionReportFiles([]);
    setConditionReportLink('');
  };


  const handleFinalSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("id", id || "");
      formData.append("vehicleName", values.vehicleName.trim().replace(/\s+/g, ' '));
      formData.append("originalPrice", values.originalPrice);
      formData.append("make", values.make);
      formData.append("model", values.model.trim().replace(/\s+/g, ' '));
      formData.append("year", values.year);
      formData.append("VIN", values.vin.trim());
      formData.append("mileage", values.mileage);
      formData.append("fuelType", values.fuelType);
      formData.append("transmission", values.transmissionType);
      formData.append("interiorColor", values.interiorColor.trim().replace(/\s+/g, ' '));
      formData.append("exteriorColor", values.exteriorColor.trim().replace(/\s+/g, ' '));

      // Categories
      if (values.category && values.category.length > 0) {
        values.category.forEach((categoryId, index) => {
          formData.append(`category[${index}]`, categoryId);
        });
      }

      // Subcategories
      if (values.subCategory && values.subCategory.length > 0) {
        values.subCategory.forEach((subCategoryId, index) => {
          formData.append(`subCategory[${index}]`, subCategoryId);
        });
      }

      formData.append("description", values.description?.trim().replace(/\s+/g, ' ') || "");
      formData.append("MVP", values.MVP?.trim().replace(/\s+/g, ' ') || "");
      formData.append("miles", values.miles || "");
      formData.append("odometerUnit", values.odometerUnit || "KM");
      formData.append("trim", values.trim?.trim() || "");
      formData.append("drivetrain", values.drivetrain?.trim() || "");
      formData.append("dealer", values.dealer || "");
      formData.append("address", values.address?.trim() || "");
      formData.append("city", values.city?.trim() || "");
      formData.append("postalCode", values.postalCode?.trim() || "");
      formData.append("country", values.country?.trim() || "");
      formData.append("lat", values.latitude || "");
      formData.append("lng", values.longitude || "");

      // --- IMAGE HANDLING ---
      images.forEach((img) => {
        if (typeof img === "string" && !img.startsWith("data:")) {
          formData.append("existingVehicleImage[]", img);
        }
      });

      imageFiles.forEach((file) => {
        formData.append("vehicleImage", file);
      });


      // Certificates as vehicleDocuments array with nested structure
      const certificateLinks = [];
      certificates.forEach((cert, index) => {
        if (cert.type === 'document' && cert.file) {
          if (cert.name) {
            formData.append(`vehicleDocuments[${index}][name]`, cert.name);
          }
          formData.append(`vehicleDocuments[${index}][image]`, cert.file);
        } else if (cert.type === 'link' && cert.link) {
          if (cert.name) {
            formData.append(`vehicleDocuments[${index}][name]`, cert.name);
          }
          certificateLinks.push(cert.link);
        }
      });

      // Append certificate links
      if (certificateLinks.length > 0) {
        certificateLinks.forEach((link, index) => {
          formData.append(`vehicleDocumentsLink[${index}]`, link);
        });
      }

      // Condition Report
      if (conditionReportType === 'document' && conditionReportFiles.length > 0) {
        formData.append("conditionReport", conditionReportFiles[0]);
      } else if (conditionReportType === 'link' && conditionReportLink) {
        formData.append("conditionReportLink", conditionReportLink);
      }

      const response = id
        ? await vehicleServices.editVehicle(formData)
        : await vehicleServices.addVehicle(formData);

      toast.success(response?.data?.message, { toastId: "success" });
      navigate(-1);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };


  const formik = useFormik({
    enableReinitialize: false,
    validationSchema: validationSchema,
    context: { images },
    initialValues: {
      vehicleName: data?.vehicleName || "",
      originalPrice: data?.originalPrice ? String(data.originalPrice) : "",
      make: data?.make?._id || data?.make || "",
      model: data?.model || "",
      year: data?.year ? String(data.year) : "",
      vin: data?.VIN || "",
      mileage: data?.mileage || "",
      fuelType: data?.fuelType || "",
      transmissionType: data?.transmission || "",
      interiorColor: data?.interiorColor || "",
      exteriorColor: data?.exteriorColor || "",
      category: data?.category ? data.category.map(cat => {
        if (typeof cat === 'string') {
          const foundCategory = categories.find(c => c.name === cat);
          return foundCategory ? foundCategory._id : cat;
        } else {
          return cat._id;
        }
      }) : [],
      subCategory: data?.subCategory ? data.subCategory.map(subCat => {
        if (typeof subCat === 'string') {
          return subCat;
        } else {
          return subCat._id;
        }
      }) : [],
      description: data?.description || "",
      MVP: data?.MVP || "",
      miles: data?.miles || "",
      odometerUnit: data?.odometerUnit || "KM",
      trim: data?.trim || "",
      drivetrain: data?.drivetrain || "",
      dealer: data?.dealer?._id || data?.dealer || "",
      address: data?.location?.address || "",
      city: data?.location?.city || "",
      postalCode: data?.location?.postalCode || "",
      country: data?.location?.country || "",
      latitude: data?.location?.coordinates?.[1] || "",
      longitude: data?.location?.coordinates?.[0] || "",
      vehicleImages: images.length > 0 ? images : imageFiles,
    },
    onSubmit: async (values) => {
      await handleFinalSubmit(values);
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue, setValues } = formik;

  // Sync vehicleImages with images state
  useEffect(() => {
    if (images.length > 0) {
      setFieldValue("vehicleImages", images);
    }
  }, [images, setFieldValue]);

  // Populate form fields when data is loaded (for edit mode)
  useEffect(() => {
    if (data && id) {
      setFieldValue("vehicleName", data.vehicleName || "");
      setFieldValue("originalPrice", data.originalPrice ? String(data.originalPrice) : "");
      setFieldValue("make", data.make?._id || data.make || "");
      setFieldValue("model", data.model || "");
      setFieldValue("year", data.year ? String(data.year) : "");
      setFieldValue("vin", data.VIN || "");
      setFieldValue("mileage", data.mileage || "");
      setFieldValue("fuelType", data.fuelType || "");
      setFieldValue("transmissionType", data.transmission || "");
      setFieldValue("interiorColor", data.interiorColor || "");
      setFieldValue("exteriorColor", data.exteriorColor || "");
      setFieldValue("description", data.description || "");
      setFieldValue("MVP", data.MVP || "");
      setFieldValue("miles", data.miles || "");
      setFieldValue("odometerUnit", data.odometerUnit || "KM");
      setFieldValue("trim", data.trim || "");
      setFieldValue("drivetrain", data.drivetrain || "");
      setFieldValue("dealer", data.dealer?._id || data.dealer || "");
      setFieldValue("address", data.location?.address || "");
      setFieldValue("city", data.location?.city || "");
      setFieldValue("postalCode", data.location?.postalCode || "");
      setFieldValue("country", data.location?.country || "");
      setFieldValue("latitude", data.location?.coordinates?.[1] || "");
      setFieldValue("longitude", data.location?.coordinates?.[0] || "");

      // Handle categories
      if (data.category && categories.length > 0) {
        const categoryIds = data.category.map(cat => {
          if (typeof cat === 'string') {
            const foundCategory = categories.find(c => c.name === cat);
            return foundCategory ? foundCategory._id : cat;
          } else {
            return cat._id;
          }
        });
        setFieldValue("category", categoryIds);
      }

      // Handle subcategories
      if (data.subCategory) {
        const subCategoryIds = data.subCategory.map(subCat => {
          if (typeof subCat === 'string') {
            return subCat;
          } else {
            return subCat._id;
          }
        });
        setFieldValue("subCategory", subCategoryIds);
      }
    }
  }, [data, id, categories, setFieldValue]);

  useEffect(() => {
    if (values.category && values.category.length > 0) {
      fetchSubCategories(values.category);
    } else {
      setSubCategories([]);
      setFieldValue("subCategory", []);
    }
  }, [values.category, fetchSubCategories, setFieldValue]);

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
  };

  const handleTrimmedChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    setFieldValue(name, trimmedValue);
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    // Keep the value as string to preserve exact input
    // Only allow digits and empty string
    if (value === '' || /^\d+$/.test(value)) {
      setFieldValue(name, value);
    }
  };

  const handleYearChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits and empty string (no decimals, no negative signs)
    if (value === '' || /^\d+$/.test(value)) {
      setFieldValue(name, value);
    }
  };


  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Vehicle`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Vehicle Images Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    Vehicle Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Drag images using the handle icon to reorder them. The first image will show first in the carousel.
                  </Typography>
                  <input
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    multiple
                    className="vehicle-form-hidden-input"
                  />

                  <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                    {images.map((image, index) => (
                      <Paper
                        key={index}
                        elevation={draggedIndex === index ? 8 : dragOverIndex === index ? 6 : 2}
                        className="vehicle-image-preview-container"
                        sx={{
                          position: 'relative',
                          cursor: draggedIndex === index ? 'grabbing' : 'default',
                          opacity: draggedIndex === index ? 0.5 : 1,
                          transform: dragOverIndex === index && draggedIndex !== null ? 'scale(1.05)' : 'scale(1)',
                          transition: 'all 0.2s ease',
                        }}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <Box
                          component="img"
                          src={image.startsWith("data:") ? image : IMAGE_BASE + image}
                          alt={`Vehicle ${index + 1}`}
                          className="vehicle-image-preview"
                          draggable={false}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                            padding: '4px',
                            cursor: 'grab',
                            '&:active': {
                              cursor: 'grabbing',
                            },
                          }}
                          title="Drag to reorder"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <SwapHorizIcon fontSize="extra-small" sx={{ color: '#666' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeImage(index)}
                          className="vehicle-image-delete-btn"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {index === 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}
                          >
                            First
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current.click()}
                    disabled={imageLoading}
                  >
                    {imageLoading ? "Uploading..." : "Upload Images"}
                  </Button>

                  {touched.vehicleImages && errors.vehicleImages && (
                    <FormHelperText error className="vehicle-form-helper-text">
                      {errors.vehicleImages}
                    </FormHelperText>
                  )}
                </Grid>

                {/* Documents Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h5" gutterBottom className="vehicle-documents-title">
                    📄 Vehicle Documents
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="vehicle-documents-subtitle">
                    Upload vehicle-related documents including certificates and condition reports
                  </Typography>
                </Grid>



                {/* Certificates */}
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" className="vehicle-certificates-title">
                      🏆 Certificates
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addNewCertificate}
                      size="small"
                      className="vehicle-add-certificate-btn"
                    >
                      Add Certificate
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" className="vehicle-documents-subtitle">
                    Upload multiple vehicle certificates with custom names
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {certificates.map((cert, index) => (
                      <Paper key={index} elevation={2} className="vehicle-certificate-paper">
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                          <Typography variant="subtitle1" className="vehicle-certificate-title">
                            Certificate {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeCertificate(index)}
                            className="vehicle-certificate-delete-btn"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <TextField
                          fullWidth
                          label="Certificate Name"
                          value={cert.name || ''}
                          onChange={(e) => updateCertificateName(index, e.target.value)}
                          placeholder="Enter certificate name (e.g., Registration Certificate)"
                          className="vehicle-certificate-textfield"
                          inputProps={{ maxLength: 100 }}
                          sx={{ mb: 2 }}
                        />

                        {/* Type Selection Buttons */}
                        <Box display="flex" gap={1} mb={2}>
                          <Button
                            variant={cert.type === 'document' ? 'contained' : 'outlined'}
                            startIcon={<CloudUploadIcon />}
                            onClick={() => handleCertificateTypeChange(index, 'document')}
                            size="small"
                            fullWidth
                            disabled={cert.type === 'document'}
                          >
                            Upload Document
                          </Button>
                          <Button
                            variant={cert.type === 'link' ? 'contained' : 'outlined'}
                            startIcon={<LinkIcon />}
                            onClick={() => handleCertificateTypeChange(index, 'link')}
                            size="small"
                            fullWidth
                            disabled={cert.type === 'link'}
                          >
                            Add Link
                          </Button>
                        </Box>

                        {/* Document Upload Section */}
                        {cert.type === 'document' && (
                          <>
                            <input
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              type="file"
                              ref={certificateInputRef}
                              onChange={(e) => handleCertificateChange(e, index)}
                              className="vehicle-form-hidden-input"
                              id={`certificate-input-${index}`}
                            />

                            <Box display="flex" flexWrap="wrap" gap={1} mb={2} minHeight={40}>
                              {cert.fileName ? (
                                <Chip
                                  label={(() => {
                                    const fileName = cert.fileName.includes('/') ? cert.fileName.split('/').pop() : cert.fileName;
                                    return fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName;
                                  })()}
                                  onClick={() => {
                                    if (cert.fileName.startsWith('public/')) {
                                      window.open(IMAGE_BASE + cert.fileName, '_blank');
                                    } else if (cert.file) {
                                      const url = URL.createObjectURL(cert.file);
                                      window.open(url, '_blank');
                                    }
                                  }}
                                  color="secondary"
                                  variant="filled"
                                  size="small"
                                  className="vehicle-certificate-chip"
                                  title={`Click to open: ${cert.fileName.includes('/') ? cert.fileName.split('/').pop() : cert.fileName}`}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary" className="vehicle-text-italic">
                                  No file uploaded
                                </Typography>
                              )}
                            </Box>

                            <Button
                              variant="contained"
                              startIcon={<CloudUploadIcon />}
                              onClick={() => document.getElementById(`certificate-input-${index}`).click()}
                              fullWidth
                              size="small"
                            >
                              Upload File
                            </Button>
                          </>
                        )}

                        {/* Link Input Section */}
                        {cert.type === 'link' && (
                          <>
                            <TextField
                              fullWidth
                              label="Document Link"
                              value={cert.link || ''}
                              onChange={(e) => handleCertificateLinkChange(index, e.target.value)}
                              placeholder="Enter document URL (e.g., https://example.com/document.pdf)"
                              className="vehicle-certificate-textfield"
                              sx={{ mb: 2 }}
                            />
                            {cert.link && (
                              <Box mb={2}>
                                <Chip
                                  label={cert.link.length > 30 ? cert.link.substring(0, 30) + '...' : cert.link}
                                  onClick={() => window.open(cert.link, '_blank')}
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  className="vehicle-certificate-chip"
                                  title={`Click to open: ${cert.link}`}
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    ))}

                    {certificates.length === 0 && (
                      <Paper elevation={1} className="vehicle-empty-state-paper">
                        <Typography variant="body2" color="text.secondary" className="vehicle-text-italic">
                          No certificates added yet. Click "Add Certificate" to get started.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Grid>

                {/* Condition Report */}
                <Grid size={{ xs: 12 }}>
                  <Paper elevation={2} className="vehicle-condition-report-paper">
                    <Box display="flex" alignItems="center" mb={2}>
                      <Typography variant="h6" className="vehicle-certificates-title">
                        📊 Condition Report
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" className="vehicle-documents-subtitle">
                      Upload a single condition report or add a link (replaces previous)
                    </Typography>

                    {/* Type Selection Buttons */}
                    <Box display="flex" gap={1} mb={2}>
                      <Button
                        variant={conditionReportType === 'document' ? 'contained' : 'outlined'}
                        startIcon={<CloudUploadIcon />}
                        onClick={() => handleConditionReportTypeChange('document')}
                        size="small"
                        fullWidth
                        disabled={conditionReportType === 'document'}
                      >
                        Upload Document
                      </Button>
                      <Button
                        variant={conditionReportType === 'link' ? 'contained' : 'outlined'}
                        startIcon={<LinkIcon />}
                        onClick={() => handleConditionReportTypeChange('link')}
                        size="small"
                        fullWidth
                        disabled={conditionReportType === 'link'}
                      >
                        Add Link
                      </Button>
                    </Box>

                    {/* Document Upload Section */}
                    {conditionReportType === 'document' && (
                      <>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          type="file"
                          ref={conditionReportInputRef}
                          onChange={handleConditionReportChange}
                          className="vehicle-form-hidden-input"
                        />

                        <Box display="flex" flexWrap="wrap" gap={1} mb={2} minHeight={60}>
                          {conditionReports.length > 0 ? (
                            <Chip
                              label={(() => {
                                const fileName = conditionReports[0].includes('/') ? conditionReports[0].split('/').pop() : conditionReports[0];
                                return fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName;
                              })()}
                              onClick={() => {
                                const fullPath = conditionReports[0].startsWith('public/') ? IMAGE_BASE + conditionReports[0] : conditionReports[0];
                                window.open(fullPath, '_blank');
                              }}
                              onDelete={removeConditionReport}
                              color="success"
                              variant="filled"
                              size="small"
                              className="vehicle-condition-report-chip"
                              title={`Click to open: ${conditionReports[0].includes('/') ? conditionReports[0].split('/').pop() : conditionReports[0]}`}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary" className="vehicle-text-italic">
                              No report uploaded
                            </Typography>
                          )}
                        </Box>

                        <Button
                          variant="contained"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => conditionReportInputRef.current.click()}
                          fullWidth
                          className="vehicle-upload-btn"
                        >
                          Upload Condition Report
                        </Button>
                      </>
                    )}

                    {/* Link Input Section */}
                    {conditionReportType === 'link' && (
                      <>
                        <TextField
                          fullWidth
                          label="Condition Report Link"
                          value={conditionReportLink}
                          onChange={(e) => setConditionReportLink(e.target.value)}
                          placeholder="Enter condition report URL (e.g., https://example.com/report.pdf)"
                          className="vehicle-certificate-textfield"
                          sx={{ mb: 2 }}
                        />
                        {conditionReportLink && (
                          <Box mb={2}>
                            <Chip
                              label={conditionReportLink.length > 30 ? conditionReportLink.substring(0, 30) + '...' : conditionReportLink}
                              onClick={() => window.open(conditionReportLink, '_blank')}
                              onDelete={removeConditionReport}
                              color="success"
                              variant="outlined"
                              size="small"
                              className="vehicle-condition-report-chip"
                              title={`Click to open: ${conditionReportLink}`}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>

                {/* Basic Vehicle Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Stock Number"
                    required
                    name="vehicleName"
                    value={values.vehicleName}
                    onChange={handleTextChange}
                    error={Boolean(touched.vehicleName && errors.vehicleName)}
                    helperText={touched.vehicleName && errors.vehicleName}
                    inputProps={{ maxLength: 100, autoComplete: 'off' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Original Price"
                    required
                    name="originalPrice"
                    type="text"
                    value={values.originalPrice || ""}
                    onChange={handlePriceChange}
                    error={Boolean(touched.originalPrice && errors.originalPrice)}
                    helperText={touched.originalPrice && errors.originalPrice}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="VIN"
                    required
                    name="vin"
                    value={values.vin}
                    onChange={handleTrimmedChange}
                    error={Boolean(touched.vin && errors.vin)}
                    helperText={touched.vin && errors.vin}
                    inputProps={{ maxLength: 17 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Year"
                    required
                    name="year"
                    type="text"
                    value={values.year}
                    onChange={handleYearChange}
                    error={Boolean(touched.year && errors.year)}
                    helperText={touched.year && errors.year}
                    inputProps={{
                      maxLength: 4,
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={brands}
                    getOptionLabel={(option) => option?.name || ''}
                    value={brands.find(brand => brand._id === values.make) || null}
                    onChange={(e, newValue) => {
                      setFieldValue("make", newValue?._id || "");
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Make"
                        required
                        placeholder="Select Brand"
                        error={Boolean(touched.make && errors.make)}
                        helperText={touched.make && errors.make}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                      />
                    )}
                    noOptionsText="No brands found"
                    loadingText="Loading brands..."
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={dealers}
                    getOptionLabel={(option) => option?.name || option?.fullName || option?.companyName || option?.email || ''}
                    value={dealers.find(dealer => dealer._id === values.dealer) || null}
                    onChange={(e, newValue) => {

                      setFieldValue("dealer", newValue?._id || "");

                      if (newValue) {
                        const addressParts = [];
                        if (newValue?.name) addressParts.push(newValue.name);
                        if (newValue?.city) addressParts.push(newValue.city);
                        if (newValue?.province) addressParts.push(newValue.province);
                        if (newValue?.postalCode) addressParts.push(newValue.postalCode);
                        if (newValue?.country) addressParts.push(newValue.country);

                        const combinedAddress = addressParts.join(", ");

                        setFieldValue("address", newValue?.address || "");
                        setFieldValue("city", newValue?.city || "");
                        setFieldValue("postalCode", newValue?.postalCode || "");
                        setFieldValue("country", newValue?.country || "");
                      } else {
                        // Clear location fields if dealer is cleared
                        setFieldValue("address", "");
                        setFieldValue("city", "");
                        setFieldValue("postalCode", "");
                        setFieldValue("country", "");
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                    loading={dealersLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dealer"
                        placeholder="Select Dealer"
                        error={Boolean(touched.dealer && errors.dealer)}
                        helperText={touched.dealer && errors.dealer}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {dealersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={dealersLoading ? "Loading dealers..." : "No dealers found"}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Model"
                    required
                    name="model"
                    value={values.model}
                    onChange={handleTextChange}
                    error={Boolean(touched.model && errors.model)}
                    helperText={touched.model && errors.model}
                    inputProps={{ maxLength: 50 }}
                    placeholder="Select model"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Trim"
                    required
                    name="trim"
                    value={values.trim}
                    onChange={handleTrimmedChange}
                    error={Boolean(touched.trim && errors.trim)}
                    helperText={touched.trim && errors.trim}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className="vehicle-odometer-container">
                    <TextField
                      className="vehicle-odometer-textfield"
                      label="Odometer"
                      required
                      name="miles"
                      type="number"
                      value={values.miles}
                      onChange={handleChange}
                      error={Boolean(touched.miles && errors.miles)}
                      helperText={touched.miles && errors.miles}
                      inputProps={{ min: 0, max: 999999 }}
                    />
                    <FormControl
                      className="vehicle-odometer-form-control"
                      error={Boolean(touched.odometerUnit && errors.odometerUnit)}
                    >
                      <InputLabel shrink className="vehicle-form-control-label">Unit *</InputLabel>
                      <Select
                        name="odometerUnit"
                        value={values.odometerUnit}
                        onChange={handleChange}
                        label="Unit *"
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span className="vehicle-select-placeholder">Select Unit</span>;
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="KM">KM</MenuItem>
                        <MenuItem value="MI">MI</MenuItem>
                      </Select>
                      {touched.odometerUnit && errors.odometerUnit && (
                        <FormHelperText>{errors.odometerUnit}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Grid>

                {/* <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Mileage (miles)"
                    required
                    name="mileage"
                    type="number"
                    value={values.mileage}
                    onChange={handleChange}
                    error={Boolean(touched.mileage && errors.mileage)}
                    helperText={touched.mileage && errors.mileage}
                    inputProps={{ min: 0, max: 999999 }}
                  />
                </Grid> */}

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={['Gas', 'Diesel', 'HYBRID', 'Electric', 'LPG/CNG']}
                    value={values.fuelType || null}
                    onChange={(e, newValue) => {
                      setFieldValue("fuelType", newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fuel Type"
                        required
                        placeholder="Select Fuel Type"
                        error={Boolean(touched.fuelType && errors.fuelType)}
                        helperText={touched.fuelType && errors.fuelType}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                      />
                    )}
                    noOptionsText="No fuel types found"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={['Automatic', 'Manual', 'CVT']}
                    value={values.transmissionType || null}
                    onChange={(e, newValue) => {
                      setFieldValue("transmissionType", newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Transmission Type"
                        required
                        placeholder="Select Transmission Type"
                        error={Boolean(touched.transmissionType && errors.transmissionType)}
                        helperText={touched.transmissionType && errors.transmissionType}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                      />
                    )}
                    noOptionsText="No transmission types found"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={['FWD', 'RWD', 'AWD', '4WD']}
                    value={values.drivetrain || null}
                    onChange={(e, newValue) => {
                      setFieldValue("drivetrain", newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Drivetrain *"
                        placeholder="Select Drivetrain"
                        error={Boolean(touched.drivetrain && errors.drivetrain)}
                        helperText={touched.drivetrain && errors.drivetrain}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                      />
                    )}
                    noOptionsText="No drivetrain options found"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Exterior Color"
                    required
                    name="exteriorColor"
                    value={values.exteriorColor}
                    onChange={handleTextChange}
                    error={Boolean(touched.exteriorColor && errors.exteriorColor)}
                    helperText={touched.exteriorColor && errors.exteriorColor}
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Interior Color"
                    required
                    name="interiorColor"
                    value={values.interiorColor}
                    onChange={handleTextChange}
                    error={Boolean(touched.interiorColor && errors.interiorColor)}
                    helperText={touched.interiorColor && errors.interiorColor}
                    inputProps={{ maxLength: 30 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    options={categories}
                    getOptionLabel={(option) => option?.name || ''}
                    value={categories.filter(cat => (values.category || []).includes(cat._id)) || []}
                    onChange={(e, newValue) => {
                      const categoryIds = newValue.map(cat => cat._id);
                      setFieldValue("category", categoryIds);
                      setFieldValue("subCategory", []); // Reset subcategory when category changes
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    loading={categoriesLoading}
                    disabled={categoriesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        required
                        placeholder="Select Category"
                        error={Boolean(touched.category && errors.category)}
                        helperText={touched.category && errors.category}
                        InputLabelProps={{
                          shrink: true,
                          className: 'vehicle-form-control-label'
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id}
                          label={option.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                    noOptionsText={categoriesLoading ? "Loading categories..." : "No categories found"}
                  />
                </Grid>

                {/* Subcategory field - shows only when category is selected */}
                {values.category && values.category.length > 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      multiple
                      options={subCategories}
                      getOptionLabel={(option) => option?.name || ''}
                      value={subCategories.filter(subCat => (values.subCategory || []).includes(subCat._id)) || []}
                      onChange={(e, newValue) => {
                        const subCategoryIds = newValue.map(subCat => subCat._id);
                        setFieldValue("subCategory", subCategoryIds);
                      }}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      loading={subCategoriesLoading}
                      disabled={subCategoriesLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub Category"
                          placeholder="Select Sub Category"
                          error={Boolean(touched.subCategory && errors.subCategory)}
                          helperText={touched.subCategory && errors.subCategory}
                          InputLabelProps={{
                            shrink: true,
                            className: 'vehicle-form-control-label'
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {subCategoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option._id}
                            label={option.name}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))
                      }
                      noOptionsText={subCategoriesLoading ? "Loading subcategories..." : "No subcategories found"}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    required
                    name="description"
                    multiline
                    rows={4}
                    value={values.description}
                    onChange={handleTextChange}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    inputProps={{ maxLength: 1000, autoComplete: 'off' }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Vehicle Options"
                    required
                    name="MVP"
                    multiline
                    rows={4}
                    value={values.MVP}
                    onChange={handleTextChange}
                    error={Boolean(touched.MVP && errors.MVP)}
                    helperText={touched.MVP && errors.MVP}
                    inputProps={{ maxLength: 1000, autoComplete: 'off' }}
                  />
                </Grid>


                {/* Location Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    Location Information
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <GooglePlacesAutocomplete
                    label="Address"
                    value={values.address}
                    onChange={(val) => setFieldValue("address", val)}
                    onPlaceSelect={(place) => {
                      console.log("Selected Place from Autocomplete:", place);
                      setValues({
                        ...values,
                        address: place.address || place.formatted_address || "",
                        city: place.city || "",
                        postalCode: place.postalCode || "",
                        country: place.country || "",
                        latitude: place.lat || "",
                        longitude: place.lng || ""
                      });
                    }}
                    onMapOpen={() => setMapModalOpen(true)}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />

                  <LocationPickerModal
                    open={mapModalOpen}
                    onClose={() => setMapModalOpen(false)}
                    initialLocation={{ lat: values.latitude, lng: values.longitude }}
                    onSelect={(place) => {
                      // console.log("Selected Location from Map:", place);
                      setValues({
                        ...values,
                        address: place.address || place.formatted_address || "",
                        city: place.city || "",
                        postalCode: place.postalCode || "",
                        country: place.country || "",
                        latitude: place.lat || "",
                        longitude: place.lng || ""
                      });
                    }}
                  />
                  {values.latitude && values.longitude && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Selected Coordinates: {values.latitude}, {values.longitude}
                    </Typography>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="City"
                    required
                    name="city"
                    value={values.city}
                    onChange={handleTrimmedChange}
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city}
                    inputProps={{ maxLength: 50, autoComplete: 'off' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    required
                    name="postalCode"
                    value={values.postalCode}
                    onChange={handleTrimmedChange}
                    error={Boolean(touched.postalCode && errors.postalCode)}
                    helperText={touched.postalCode && errors.postalCode}
                    inputProps={{ maxLength: 20, autoComplete: 'off' }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Country"
                    required
                    name="country"
                    value={values.country}
                    onChange={handleTrimmedChange}
                    error={Boolean(touched.country && errors.country)}
                    helperText={touched.country && errors.country}
                    inputProps={{ maxLength: 50, autoComplete: 'off' }}
                  />
                </Grid>



                {/* Submit button */}
                <Grid size={{ xs: 12 }}>
                  <Box mt={3}>
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Vehicle
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditVehicle;


